import type { WorkflowType } from '@communities/content-management/domain';
import { SECTION_DESCRIPTIONS } from '@communities/content-management/utils/sectionDescriptions';
import { Text, Heading, Dialog } from '@soteria/design-system-ve';

import * as styles from './MoreInfoDialog.css';

interface WorkflowMoreInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workflowType: WorkflowType;
}

/**
 * Unified info dialog component for all workflow types
 * Provides detailed information about how different workflow types work
 * based on the workflowType prop
 */
export function WorkflowMoreInfoDialog({ isOpen, onClose, workflowType }: WorkflowMoreInfoDialogProps) {
  // Get the appropriate section description based on workflow type
  const getSectionDescription = () => {
    switch (workflowType) {
      case 'classification':
        return SECTION_DESCRIPTIONS.CLASSIFICATION_WORKFLOWS;
      case 'reports':
        return SECTION_DESCRIPTIONS.REPORTING_WORKFLOWS;
      default:
        return SECTION_DESCRIPTIONS.CLASSIFICATION_WORKFLOWS;
    }
  };

  // Get workflow-specific content
  const getWorkflowContent = () => {
    switch (workflowType) {
      case 'classification':
        return {
          introduction: (
            <Text>
              Classification workflows automatically categorize and evaluate content based on your community guidelines. They combine <strong>topics</strong>{' '}
              and <strong>actions</strong> to create complete moderation processes.
            </Text>
          ),
          topicsDescription: (
            <Text>
              <strong>Topics</strong> define what content to classify (e.g., chat messages, user uploads, forum posts)
            </Text>
          ),
          actionsDescription: (
            <Text>
              <strong>Actions</strong> determine what happens when classification detects policy violations
            </Text>
          ),
          workflowsDescription: (
            <Text>
              <strong>Workflows</strong> connect topics to actions, creating automated moderation pipelines
            </Text>
          ),
          benefits: [
            'Automatically detect policy violations and harmful content',
            'Reduce manual moderation workload through intelligent pre-screening',
            'Ensure consistent content evaluation across your community',
            'Configure API settings for optimal performance and throughput',
          ],
          conclusion: (
            <Text>
              Classification workflows are essential for maintaining community safety at scale, providing the first line of defense against inappropriate
              content.
            </Text>
          ),
        };
      case 'reports':
        return {
          introduction: (
            <Text>
              Reporting workflows handle user-generated reports and complaints about content or behavior in your community. They combine <strong>topics</strong>{' '}
              and <strong>actions</strong> to create systematic report processing.
            </Text>
          ),
          topicsDescription: (
            <Text>
              <strong>Topics</strong> define what types of content can be reported (e.g., harassment, spam, inappropriate content)
            </Text>
          ),
          actionsDescription: (
            <Text>
              <strong>Actions</strong> determine what happens when reports are validated (e.g., content removal, user warnings, escalation)
            </Text>
          ),
          workflowsDescription: (
            <Text>
              <strong>Workflows</strong> connect reported topics to appropriate actions, ensuring consistent report handling
            </Text>
          ),
          benefits: [
            'Systematically process user reports and complaints',
            'Track report status and resolution outcomes',
            'Generate insights and analytics on community issues',
            'Configure API settings for report processing efficiency',
          ],
          conclusion: (
            <Text>
              Reporting workflows ensure that community concerns are addressed promptly and fairly, maintaining trust and safety within your community.
            </Text>
          ),
        };
      default:
        // Fallback to classification workflow content
        return getWorkflowContent();
    }
  };

  const sectionDescription = getSectionDescription();
  const content = getWorkflowContent();

  return (
    <Dialog cancelAction={{ label: 'Close' }} isOpen={isOpen} size="xxxl" title={sectionDescription.title} onClose={onClose}>
      <div className={styles.dialogContent}>
        {content.introduction}

        <div className={styles.section}>
          <Heading level="h3">How Topics & Actions Work Together:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>{content.topicsDescription}</li>
            <li className={styles.listItem}>{content.actionsDescription}</li>
            <li className={styles.listItem}>{content.workflowsDescription}</li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Key benefits:</Heading>
          <ul className={styles.list}>
            {content.benefits.map(benefit => (
              <li key={benefit} className={styles.listItem}>
                <Text>{benefit}</Text>
              </li>
            ))}
          </ul>
        </div>

        {content.conclusion}
      </div>
    </Dialog>
  );
}
